import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import PublicLayout from "@/components/marketing/PublicLayout";
import Home from "@/pages/marketing/Home";
import Services from "@/pages/marketing/Services";
import Reviews from "@/pages/marketing/Reviews";
import About from "@/pages/marketing/About";
import Contact from "@/pages/marketing/Contact";
import PrivacyPolicy from "@/pages/marketing/PrivacyPolicy";
import TermsAndConditions from "@/pages/marketing/TermsAndConditions";
import Partner from "@/pages/marketing/Partner";
import RequestAccess from "@/pages/marketing/RequestAccess";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import PropertyLookup from "@/pages/PropertyLookup";
import PropertySearch from "@/pages/PropertySearch";
import Boards from "@/pages/Boards";
import Buyers from "@/pages/Buyers";
import Offers from "@/pages/Offers";
import ProfitCalculator from "@/pages/ProfitCalculator";
import Properties from "@/pages/Properties";
import LeadSources from "@/pages/LeadSources";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import AdminDashboard from "@/pages/AdminDashboard";
import Communications from "@/pages/Communications";
import SmsInbox from "@/pages/SmsInbox";
import LeadLists from "@/pages/LeadLists";
import LeadFinder from "@/pages/LeadFinder";
import NotFound from "@/pages/NotFound";
import Team from "@/pages/Team";
import AcceptInvite from "@/pages/AcceptInvite";
import DrivingForDollars from "@/pages/DrivingForDollars";
import SmsBlaster from "@/pages/SmsBlaster";
import AiPhone from "@/pages/AiPhone";
import SkipTrace from "@/pages/SkipTrace";
import Dialer from "@/pages/Dialer";
import AILeadManager from "@/pages/AILeadManager";
import AICallInbox from "@/pages/AICallInbox";
import ArchivedLeads from "@/pages/ArchivedLeads";
import RepActivity from "@/pages/RepActivity";
import CallMonitor from "@/pages/CallMonitor";
import TeamLeaderboard from "@/pages/TeamLeaderboard";
import TeamHub from "@/pages/TeamHub";
import RepairEstimator from "@/pages/RepairEstimator";
import AdminMarketingLeads from "@/pages/AdminMarketingLeads";
import AdminAccessRequests from "@/pages/AdminAccessRequests";
import LiveDialing from "@/pages/LiveDialing";
import Reporting from "@/pages/Reporting";
import DialerPerformance from "@/pages/DialerPerformance";
import CreateNewLead from "@/pages/CreateNewLead";
import LeadInformation from "@/pages/LeadInformation";
import PublicLeadCapture from "@/pages/PublicLeadCapture";

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public marketing site */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/request-access" element={<RequestAccess />} />
          </Route>

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* Public lead-capture — no auth. Cold callers open this URL
              from the admin-generated token and submit leads directly
              into the tenant's board/column. */}
          <Route path="/capture/:token" element={<PublicLeadCapture />} />
          <Route path="/public/lead-capture/:token" element={<PublicLeadCapture />} />

          {/* Authenticated app */}
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/lookup" element={<PropertyLookup />} />
            <Route path="/search" element={<PropertySearch />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/leads" element={<LeadSources />} />
            <Route path="/pipeline" element={<Navigate to="/boards" replace />} />
            <Route path="/boards" element={<Boards />} />
            <Route path="/offers" element={<Offers />} />
            <Route path="/profit-calculator" element={<ProfitCalculator />} />
            <Route path="/buyers" element={<Buyers />} />
            <Route path="/communications" element={<Communications />} />
            <Route path="/sms-inbox" element={<SmsInbox />} />
            <Route path="/lists" element={<LeadLists />} />
            <Route path="/lead-finder" element={<LeadFinder />} />
            <Route path="/d4d" element={<DrivingForDollars />} />
            <Route path="/sms-blaster" element={<SmsBlaster />} />
            <Route path="/dispo" element={<Navigate to="/boards?tab=dispo" replace />} />
            <Route path="/ai-phone" element={<AiPhone />} />
            <Route path="/skip-trace" element={<SkipTrace />} />
            <Route path="/ai-lead-manager" element={<AILeadManager />} />
            <Route path="/ai-call-inbox" element={<AICallInbox />} />
            <Route path="/archived-leads" element={<ArchivedLeads />} />
            <Route path="/rep-activity" element={<RepActivity />} />
            <Route path="/call-monitor" element={<CallMonitor />} />
            <Route path="/dialer" element={<Dialer />} />
            <Route path="/dialer-performance" element={<DialerPerformance />} />
            <Route path="/leads/new" element={<CreateNewLead />} />
            <Route path="/leads/:id" element={<LeadInformation />} />
            <Route path="/team" element={<TeamHub />} />
            <Route path="/leaderboard" element={<Navigate to="/team?tab=leaderboard" replace />} />
            <Route path="/repair-estimator" element={<RepairEstimator />} />
            <Route path="/admin/marketing-leads" element={<AdminMarketingLeads />} />
            <Route path="/admin/access-requests" element={<AdminAccessRequests />} />
            <Route path="/admin/live-dialing" element={<LiveDialing />} />
            <Route path="/reports" element={<Reporting />} />
            <Route path="/reporting" element={<Navigate to="/reports" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Route>

          {/* Catch-all — friendly branded 404 for any unmatched URL */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
